/**
 * The look every dropdown in this app shares, in one place.
 *
 * `Combobox` and `ShareWithMembersField` cannot share their markup — one picks
 * a single value, the other toggles several and carries a count — but there is
 * no reason for them to look different, and twice now they quietly had: a
 * bigger, darker label on one, a filled triangle instead of a chevron, a
 * placeholder one shade lighter. Those are the kind of differences nobody
 * notices while writing and everybody notices side by side.
 */

/** Sits above the control. A `<p>`, not a `<label>`: these controls are buttons, and `htmlFor` does nothing for one. */
export const COMBOBOX_LABEL_CLASS =
  'mb-1 block text-xs font-semibold text-[var(--text-secondary)] sm:mb-1.5'

/** The closed control. */
export const COMBOBOX_TRIGGER_CLASS =
  'input-field flex w-full items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60'

/**
 * The closed control where it sits inline beside a heading instead of in a
 * form: it shrinks to its content and caps itself, a project code being a few
 * characters while the label beside it can be a sentence.
 */
export const COMBOBOX_TRIGGER_COMPACT_CLASS =
  'input-field flex w-auto max-w-[220px] items-center gap-1.5 rounded-lg px-2.5 py-1 text-left text-xs disabled:cursor-not-allowed disabled:opacity-60'

/** The text inside it, which truncates rather than letting the control grow. */
export const COMBOBOX_VALUE_CLASS = 'min-w-0 flex-1 truncate'

/** Applied to that text while nothing is chosen. */
export const COMBOBOX_PLACEHOLDER_CLASS = 'text-[var(--text-secondary)]'

export const COMBOBOX_CHEVRON_CLASS = 'h-4 w-4 shrink-0 text-[var(--text-secondary)]'

/** The floating panel. Where it is drawn comes from `useComboboxPanel`. */
export const COMBOBOX_PANEL_CLASS =
  'overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl'

export const COMBOBOX_SEARCH_WRAP_CLASS = 'border-b border-[var(--border-color)] p-2'

export const COMBOBOX_SEARCH_CLASS =
  'w-full rounded-lg bg-[var(--bg-tertiary)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] outline-none'

export const COMBOBOX_LIST_CLASS = 'max-h-56 overflow-y-auto py-1'

/** Loading, error and empty rows inside the list. */
export const COMBOBOX_NOTICE_CLASS = 'px-3 py-4 text-center text-sm text-[var(--text-secondary)]'
